const {
  buildProperties,
  fetchJSON,
  getDefaultRequestConfig,
  getElement,
} = window.BAO.Utils

const defaultSectionsUrl = () => {
  const appProxyUrls = ['/apps/', '/a/', '/tools/', '/community/']

  if (appProxyUrls.some((url) => window.location.pathname.includes(url))) {
    return '/'
  }

  return window.location.pathname
}

/** @const {string} The context in which we want the sections to be rendered in */
const DEFAULT_SECTIONS_URL = defaultSectionsUrl()
const RECOMMENDATION_HISTORY_KEY = 'push_cart_recommendation_history'
const RECOMMENDATIONS_SECTION = 'push-cart-recommendations'

/**
 * @typedef {Object} PotentialCartItem
 *
 * @property {number} id The variant's unique ID
 * @property {number} quantity The quantity of items to be added to the cart
 * @property {Object} [properties] Line item property key/values
 */

/**
 * @typedef {Object} BaoCartSectionOptions
 *
 * @property {string[]} [sections] - The sections we want rendered alongside any cart
 *   changes
 * @property {string} [sectionsUrl] - The context in which we want the sections to be
 *   rendered in in
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithAttributes
 *
 * @property {Object} [attributes] Key/values that we want to add to the cart attributes
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithForm
 *
 * @property {HTMLFormElement} [form] The form that has added the item
 */

/**
 * By Association Only's cart helper methods
 */
class BaoCart {
  constructor () {
    this._recommendationHistory = []

    const items = this.cartItemsFromDOM()
    this.syncRecommendationHistory(items, {
      preserveSource: items.length > 0,
    })
  }

  /**
   * @param {HTMLFormElement} form
   * @param {BaoCartSectionOptionsWithAttributes} options Optional values to pass to /cart/add.js.
   *   Set `preventRender` when the item being added is only correct on screen
   *   *after* Gift With Purchase has finished reconciling (e.g. a gift picked
   *   from the selection modal, which lands at full price until its discount
   *   code is applied); the drawer then waits for the GWP pass to render, as
   *   it always used to. Leave it `false` for an ordinary add so the drawer
   *   paints as soon as the add itself returns.
   *
   * @returns {Promise<Object>}
   */
  async addItemFromForm (
    form,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      preventRender = false,
    } = {},
  ) {
    const formData = new FormData(form)
    const isRecommendationAdd = this.isRecommendationItem({
      properties: buildProperties(formData),
    })

    if (!isRecommendationAdd) {
      sections = window.BAO.sectionRenderer.withoutRecommendations(sections)
    }

    formData.append('sections', sections.join(','))
    formData.append('sections_url', sectionsUrl)

    window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM, {
      form,
      formData,
    })

    const config = getDefaultRequestConfig('javascript')
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    config.body = formData

    const variantId = formData.get('id')

    try {
      const addResponse = await fetchJSON(`${window.routes.cartAddUrl}`, config)

      if (isRecommendationAdd) {
        const addedItems = Array.isArray(addResponse.items)
          ? addResponse.items
          : [addResponse]
        this.trackRecommendationSources([{ id: variantId }], addedItems)
      }

      const sectionsData = addResponse.sections

      // Paint the customer's item into the drawer the moment the add itself
      // comes back. The `getCart` below is only needed so the dispatched state
      // carries `discount_codes` and the full `items` list, which nothing but
      // the Gift With Purchase reconcile pass consumes, and that pass can take
      // several more round trips on top. Waiting for all of it before showing
      // anything is what made a gift-eligible add feel slow.
      const renderImmediately = !preventRender && !!sectionsData

      if (renderImmediately) {
        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.RENDER, {
          state: { sections: sectionsData },
        })
      }

      const fullCartState = await this.getCart()
      fullCartState.sections = sectionsData

      if (isRecommendationAdd) {
        this.syncRecommendationHistory(fullCartState.items)
      }

      const state = fullCartState

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
        {
          form,
          formData,
          state,
        },
      )

      // Already painted above, so suppress the duplicate swap of identical
      // HTML. AFTER_RENDER still fires, which is what hands over to GWP.
      // Falling back to the original `shouldRun` check keeps the previous
      // behaviour whenever the early paint didn't happen.
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
        preventRender:
          renderImmediately || BAO.GiftWithPurchase.shouldRun(state),
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err, variantId)
    }
  }

  /**
   * @param {PotentialCartItem[]|PotentialCartItem} items
   * @param {BaoCartSectionOptionsWithAttributes & BaoCartSectionOptionsWithForm} options Optional
   *   values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItem (
    items,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      attributes = {},
      form,
      silent = false,
      mergeCart = true,
    } = {},
  ) {
    if (!Array.isArray(items)) {
      items = [items]
    }

    const recommendationItems = items.filter((item) => (
      this.isRecommendationItem(item)
    ))

    if (recommendationItems.length === 0) {
      sections = window.BAO.sectionRenderer.withoutRecommendations(sections)
    }

    if (!silent) {
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.BEFORE_ADD, {
        items,
        attributes,
        form,
      })
    }

    const body = {
      items,
      attributes,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartAdd(body, { mergeCart })

      if (recommendationItems.length > 0) {
        if (mergeCart) {
          this.syncRecommendationHistory(state.items)
        }
        this.trackRecommendationSources(recommendationItems, state.items)
      }

      if (!silent) {
        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.AFTER_ADD, {
          items,
          state,
          form,
        })

        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
          state,
        })
      }

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The amount of the item we want change the line item to. Can be 0.
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateQuantity (
    key,
    quantity,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const previousItems = this.cartItemsFromDOM()

    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_ITEM_QUANTITY,
      {
        key,
        quantity,
      },
    )
    const body = {
      id: key,
      quantity,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      let state = await this.cartChange(body)
      state = await this.processFreeSamples(state)

      if (quantity === 0) {
        this.syncRecommendationHistoryAfterRemoval(
          state.items,
          [key],
          previousItems,
        )
      }

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY,
        {
          state,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
        preventRender: BAO.GiftWithPurchase.shouldRun(state),
      })

      return state
    } catch (err) {
      console.error(err)
      // Return current cart state on error instead of undefined
      return await this.getCart()
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {BaoCartSectionOptions & {
   *   silent?: boolean,
   *   preserveRecommendationSource?: boolean
   * }} options
   *
   * @returns {Promise<Object>}
   */
  async removeItem (
    key,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      silent = false,
      preserveRecommendationSource = false,
    } = {},
  ) {
    const previousItems = this.cartItemsFromDOM()

    try {
      let state = await this.cartChange({
        id: key,
        quantity: 0,
        sections,
        sections_url: sectionsUrl,
      })

      // If the remove left only a free sample behind, clean it up.
      const nonSampleCount = state.items.filter(item => !item.properties?._freeSample).length
      if (nonSampleCount === 0) {
        const sample = state.items.find(item => item.properties?._freeSample)

        if (sample) {
          state = await this.cartChange({
            id: sample.key,
            quantity: 0,
            sections,
            sections_url: sectionsUrl,
          })
        }
      }

      if (!preserveRecommendationSource) {
        this.syncRecommendationHistoryAfterRemoval(
          state.items,
          [key],
          previousItems,
        )
      }

      if (!silent) {
        window.BAO.dispatchEvent(
          window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
          {
            state,
          },
        )

        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
          state,
          preventRender: BAO.GiftWithPurchase.shouldRun(state),
        })
      }

      return state
    } catch (err) {
      console.error('Error in removeItem:', err)
      // Return current cart state on error instead of undefined
      return await this.getCart()
    }
  }

  /**
   * @param {string[]} keys The key of the line item we want to change
   * @param {BaoCartSectionOptions & { skipGwp?: boolean }} [options]
   *
   * @returns {Promise<Object>}
   */
  async removeItems (
    keys,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      skipGwp = false,
    } = {},
  ) {
    const previousItems = this.cartItemsFromDOM()

    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM,
      { keys },
    )

    const body = {
      updates: Object.fromEntries(keys.map(k => [k, 0])),
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      let state = await this.cartUpdate(body)
      state = await this.processFreeSamples(state)

      if (state.items.length === 0) {
        this.setRecommendationHistory([])
      } else if (!skipGwp) {
        this.syncRecommendationHistoryAfterRemoval(
          state.items,
          keys,
          previousItems,
        )
      }

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
        {
          state,
        },
      )

      if (!skipGwp) {
        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
          state,
          preventRender: BAO.GiftWithPurchase.shouldRun(state),
        })
      }

      return state
    } catch (err) {
      console.error(err)

      return await this.getCart()
    }
  }

  async processFreeSamples(state, { silent = true } = {}) {
    const sampleLimit = window.theme.freeSampleThreshold
    const sampleVariantId = window.theme.freeSampleVariantId
    const sections = window.BAO.sectionRenderer.pushCartSections.map(s => s.section)
    const sectionsUrl = DEFAULT_SECTIONS_URL

    const qualifies = state.original_total_price >= sampleLimit
    const sampleItem = state.items.find(i => i.properties?._freeSample === 'true')

    if (!qualifies && sampleItem) {
      state = await window.BAO.Cart.removeItem(sampleItem.key, {
        sections,
        sectionsUrl,
        silent,
      })
    }

    if (qualifies && !sampleItem && sampleVariantId) {
      state = await window.BAO.Cart.addItem({
        id: sampleVariantId,
        quantity: 1,
        properties: {
          _freeSample: 'true',
        },
      }, {
        silent: true,
        sections,
        sectionsUrl,
      })
    }

    return state
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The quantity of items to be added to the cart. If this isn't set then Shopify will reset it back to 1
   * @param {object} properties An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateItemProperties (
    key,
    quantity,
    properties,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const body = { id: key, properties, quantity }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      return this.cartChange(body)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {object} attributes An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateCartAttributes (
    attributes,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        s => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_CART_ATTRIBUTES,
      {
        attributes,
      },
    )

    const body = { attributes }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_CART_ATTRIBUTES,
        {
          attributes,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
        preventRender: BAO.GiftWithPurchase.shouldRun(state),
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {string} note The note we want to set
   *
   * @returns {Promise<Object>}
   */
  async updateNote (note) {
    window.BAO.dispatchEvent(window.BAO.EVENTS.CART_NOTE.BEFORE_CHANGE, {
      note,
    })

    try {
      const state = await this.cartUpdate({ note })

      window.BAO.dispatchEvent(window.BAO.EVENTS.CART_NOTE.AFTER_CHANGE, {
        state,
        note,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  handleAddToCartError (res, variantId = null) {
    /**
     * @type {PushCart}
     */
    const pushCart = getElement('push-cart')
    if (!pushCart) return

    pushCart.handleError(res, variantId)
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   * @param {object} [options]
   * @param {boolean} [options.mergeCart] Fetch and merge in full cart state
   *   (needed so dispatched state carries `discount_codes`, since
   *   `/cart/add.js` only returns the added line items). Set to `false` only
   *   for callers that discard the returned state and refetch immediately
   *   themselves.
   *
   * @returns {Promise<Object>}
   */
  async cartAdd (body, { mergeCart = true } = {}) {
    const addResponse = await fetchJSON(`${window.routes.cartAddUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })

    if (!mergeCart) return addResponse

    const cart = await this.getCart()
    cart.sections = addResponse.sections

    return cart
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartChange (body) {
    return fetchJSON(`${window.routes.cartChangeUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartUpdate (body) {
    return fetchJSON(`${window.routes.cartUpdateUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  async getCart () {
    return fetchJSON('/cart.js', {
      ...getDefaultRequestConfig(),
      method: 'GET'
    })
  }

  /**
   * The default `sections_url` used by every cart mutation that renders
   * sections, including app-proxy-page special-casing. Exposed so callers
   * building a raw `cartChange`/`cartUpdate` body (rather than going through
   * a wrapper method that already applies this default) stay consistent.
   *
   * @returns {string}
   */
  get sectionsUrl () {
    return DEFAULT_SECTIONS_URL
  }

  /**
   * @returns {string[]}
   */
  getRecommendationHistory () {
    try {
      const storedHistory = sessionStorage.getItem(RECOMMENDATION_HISTORY_KEY)
      if (storedHistory == null) return [...this._recommendationHistory]

      const history = JSON.parse(storedHistory)
      this._recommendationHistory = Array.isArray(history)
        ? history.map(String)
        : []

      return [...this._recommendationHistory]
    } catch (error) {
      return [...this._recommendationHistory]
    }
  }

  /**
   * @param {Array<string|number>} history
   */
  setRecommendationHistory (history) {
    const normalisedHistory = [...new Set(history.filter(Boolean).map(String))]
    this._recommendationHistory = normalisedHistory

    try {
      if (normalisedHistory.length === 0) {
        sessionStorage.removeItem(RECOMMENDATION_HISTORY_KEY)
        return
      }

      sessionStorage.setItem(
        RECOMMENDATION_HISTORY_KEY,
        JSON.stringify(normalisedHistory),
      )
    } catch (error) {
      // Session storage can be unavailable in privacy-restricted browsers.
    }
  }

  /**
   * Keep the stored recency order, remove products no longer paid lines, and
   * append any untracked paid products in Shopify's current cart order.
   *
   * @param {object[]} items
   * @param {{ preserveSource?: boolean }} [options]
   * @returns {string|null}
   */
  syncRecommendationHistory (
    items = [],
    { preserveSource = false } = {},
  ) {
    const eligibleProductIds = [...new Set(
      items
        .filter((item) => this.isRecommendationItem(item))
        .map((item) => item.product_id)
        .filter(Boolean)
        .map(String),
    )]
    const currentProductIds = new Set(
      items
        .map((item) => item.product_id)
        .filter(Boolean)
        .map(String),
    )
    const currentHistory = this.getRecommendationHistory()
    const nextHistory = currentHistory.filter((id) => (
      eligibleProductIds.includes(id) ||
      (preserveSource && currentProductIds.has(id))
    ))

    eligibleProductIds.forEach((id) => {
      if (!nextHistory.includes(id)) nextHistory.push(id)
    })

    this.setRecommendationHistory(nextHistory)

    return nextHistory[0] ?? null
  }

  /**
   * Remove only products targeted by this paid-line mutation. Other missing
   * products may be intentionally retained after a paid line was converted
   * into a GWP line, whose add/remove must preserve recommendations.
   *
   * @param {object[]} items
   * @param {string[]} keys
   * @param {object[]} previousItems
   * @returns {string|null}
   */
  syncRecommendationHistoryAfterRemoval (items, keys, previousItems) {
    if (items.length === 0) {
      this.setRecommendationHistory([])
      return null
    }

    const eligibleProductIds = [...new Set(
      items
        .filter((item) => this.isRecommendationItem(item))
        .map((item) => item.product_id)
        .filter(Boolean)
        .map(String),
    )]
    const removedProductIds = previousItems
      .filter((item) => (
        keys.includes(item.key) && this.isRecommendationItem(item)
      ))
      .map((item) => String(item.product_id))
    const history = this.getRecommendationHistory().filter((id) => (
      !removedProductIds.includes(id) || eligibleProductIds.includes(id)
    ))

    eligibleProductIds.forEach((id) => {
      if (!history.includes(id)) history.push(id)
    })

    this.setRecommendationHistory(history)

    return history[0] ?? null
  }

  /**
   * @param {object} item
   */
  trackRecommendationSource (item) {
    if (!this.isRecommendationItem(item)) return
    if (!item?.product_id) return

    const productId = String(item.product_id)
    const history = this.getRecommendationHistory()

    this.setRecommendationHistory([
      productId,
      ...history.filter((id) => id !== productId),
    ])
  }

  /**
   * @param {PotentialCartItem[]} potentialItems
   * @param {object[]} cartItems
   */
  trackRecommendationSources (potentialItems, cartItems = []) {
    potentialItems.forEach((potentialItem) => {
      if (!this.isRecommendationItem(potentialItem)) return

      const item = cartItems.find((cartItem) => (
        this.isRecommendationItem(cartItem) &&
        String(cartItem.variant_id ?? cartItem.id) === String(potentialItem.id)
      ))

      this.trackRecommendationSource(item)
    })
  }

  /**
   * @param {object} item
   * @returns {boolean}
   */
  isRecommendationItem (item) {
    if (!item) return false

    if (item.recommendationEligible != null) {
      return item.recommendationEligible === true ||
        item.recommendationEligible === 'true'
    }

    const properties = item.properties ?? {}

    return properties._gwp !== 'true' &&
      properties._freeSample !== 'true' &&
      properties._complimentary !== 'true' &&
      !properties.__giftWithPurchase
  }

  /**
   * @returns {object[]}
   */
  cartItemsFromDOM () {
    return Array.from(
      document.querySelectorAll('push-cart cart-item[data-product-id]'),
    ).map((item) => ({
      key: item.dataset.key,
      product_id: item.dataset.productId,
      quantity: Number(item.querySelector('[name="quantity"]')?.value ?? 0),
      recommendationEligible:
        item.dataset.recommendationEligible === 'true',
    }))
  }

  /**
   * @param {object[]} items
   * @returns {Map<string, number>}
   */
  recommendationQuantities (items) {
    return items.reduce((quantities, item) => {
      if (!this.isRecommendationItem(item) || !item.product_id) {
        return quantities
      }

      const productId = String(item.product_id)
      const quantity = quantities.get(productId) ?? 0
      quantities.set(productId, quantity + Number(item.quantity ?? 0))

      return quantities
    }, new Map())
  }

  /**
   * Remove recommendation HTML from a render when its paid source has not
   * changed. This prevents reconnecting the custom element and re-fetching
   * identical recommendations after GWP or older-line changes.
   *
   * @param {object} state
   * @returns {object}
   */
  prepareSectionsForRender (state) {
    if (!state?.sections) return state

    const sections = { ...state.sections }

    if (
      RECOMMENDATIONS_SECTION in sections &&
      !this.recommendationsNeedRender()
    ) {
      delete sections[RECOMMENDATIONS_SECTION]
    }

    return { ...state, sections }
  }

  /**
   * @returns {boolean}
   */
  recommendationsNeedRender () {
    const source = this.recommendationSourceProductId
    const renderedSource = this.renderedRecommendationProductId

    if (source !== renderedSource) return true
    if (source != null) return false

    return Array.from(document.querySelectorAll('.psh-Recommendations'))
      .some((container) => container.innerHTML.trim().length > 0)
  }

  /**
   * @returns {string|null}
   */
  get recommendationSourceProductId () {
    return this.getRecommendationHistory()[0] ?? null
  }

  /**
   * @returns {string|null}
   */
  get renderedRecommendationProductId () {
    const productId = document
      .querySelector('push-cart-recommendations')
      ?.getAttribute('data-product-id')

    return productId ? String(productId) : null
  }

  /**
   * Cart JSON plus fresh push-cart section HTML, without dispatching CHANGED.
   * Used by Gift With Purchase after silent mutations so the drawer can render
   * discounted prices in one pass.
   *
   * @returns {Promise<Object>} The updated cart state
   */
  async getCartWithSections () {
    const sections = window.BAO.sectionRenderer.pushCartSections.map(s => s.section)
    const [sectionsHtml, state] = await Promise.all([
      window.BAO.sectionRenderer.fetch(sections),
      this.getCart(),
    ])
    state.sections = sectionsHtml
    return state
  }

  /**
   * Refreshes the cart sections after an external script has added an item to the cart
   *
   * @param {object} [details] Added product information from the external event
   * @returns {Promise<Object>} The updated cart state
   */
  async refreshCart (details = {}) {
    const refreshVersion = (this.refreshVersion ?? 0) + 1
    this.refreshVersion = refreshVersion

    try {
      const currentQuantities = this.recommendationQuantities(
        this.cartItemsFromDOM(),
      )
      const state = await this.getCartWithSections()
      if (refreshVersion !== this.refreshVersion) return state

      const sourceDetails = details ?? {}
      const productId = sourceDetails.productId ?? sourceDetails.product_id
      const variantId = sourceDetails.variantId ?? sourceDetails.variant_id
      const nextQuantities = this.recommendationQuantities(state.items)
      const explicitItem = state.items.find((item) => (
        this.isRecommendationItem(item) &&
        (
          (productId && String(item.product_id) === String(productId)) ||
          (variantId && String(item.variant_id ?? item.id) === String(variantId))
        )
      ))
      const latestNewItem = explicitItem ?? state.items.find((item) => (
        this.isRecommendationItem(item) &&
        (nextQuantities.get(String(item.product_id)) ?? 0) >
          (currentQuantities.get(String(item.product_id)) ?? 0)
      ))

      this.syncRecommendationHistory(state.items)
      this.trackRecommendationSource(latestNewItem)

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })
      return state
    } catch (err) {
      console.error('Error refreshing cart:', err)
    }
  }

  /**
   * @param {string} discount A comma separated list of discount codes
   *
   * @returns {Promise<Object>}
   */
  /**
   * @param {string} discount A comma separated list of discount codes
   * @param {BaoCartSectionOptions} [options]
   *
   * @returns {Promise<Object>}
   */
  async updateDiscount (
    discount,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(s => s.section),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.CART_DISCOUNT.BEFORE_CHANGE,
      {
        discount,
      },
    )

    try {
      const body = { discount }

      if (sections.length) {
        body.sections = sections
        body.sections_url = sectionsUrl
      }

      let state = await this.cartUpdate(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.CART_DISCOUNT.AFTER_CHANGE,
        {
          state,
          discount,
        },
      )

      return state
    } catch (e) {
      console.error(e)
    }
  }
}

window.BAO.Cart = new BaoCart()
